import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    inject,
    Injector,
    LOCALE_ID,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideMicroSentry } from '@micro-sentry/angular';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { provideTranslateCompiler, provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideSweetAlert2 } from '@sweetalert2/ngx-sweetalert2';
import { AppHttpInterceptor } from 'app-http-interceptor';
import { fooswarsAppInitializer } from 'core/app-initializer';
import { AuthInterceptor } from 'core/auth.interceptor';
import { BrowserStorageService } from 'core/browser-storage.service';
import { MESSAGE_FORMAT_CONFIG, TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { effects, metaReducers, reducers } from 'store';
import { appRoutes } from './app.routes';
import { ApplicationErrorHandler } from './app/error-handler';
import { environment } from './environments/environment';

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection(),
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: 'assets/i18n/',
                suffix: `.json?v=${environment.version}`,
            }),
            compiler: provideTranslateCompiler(TranslateMessageFormatCompiler),
        }),
        provideServiceWorker('/ngsw-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerWhenStable:30000',
        }),
        provideSweetAlert2({
            provideSwal: () =>
                import('sweetalert2').then(({ default: swal }) =>
                    swal.mixin({
                        backdrop: false,
                        theme: 'dark',
                    }),
                ),
        }),
        importProvidersFrom(
            StoreModule.forRoot(reducers, {
                metaReducers: metaReducers,
                runtimeChecks: {
                    strictActionImmutability: false,
                    strictStateImmutability: false,
                },
            }),
            EffectsModule.forRoot(effects),
        ),
        {
            provide: LOCALE_ID,
            deps: [TranslateService],
            useFactory: (tran: TranslateService) => tran.getCurrentLang() || 'cs',
        },
        {
            provide: ErrorHandler,
            useClass: ApplicationErrorHandler,
            deps: [Injector],
        },
        { provide: HTTP_INTERCEPTORS, useClass: AppHttpInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: MESSAGE_FORMAT_CONFIG, useValue: { locales: ['cs', 'en'] } },
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        provideAppInitializer(() => {
            const initializerFn = fooswarsAppInitializer(inject(BrowserStorageService));
            return initializerFn();
        }),
        provideHttpClient(withInterceptorsFromDi()),
        provideRouter(appRoutes),
        provideAnimations(),
        provideLuxonDateAdapter(),
        provideMicroSentry({
            dsn: environment.dsn,
            environment: environment.production ? 'production' : 'development',
            release: environment.version,
        }),
    ],
};
